import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import { db } from '../../firebase';
import { getDocs, collection, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuth } from '../../context/AuthContext';

const ITEMS_PER_PAGE = 10;

const EMPTY_USER = {
  displayName: '',
  email: '',
  password: '',
  role: 'member',
  branchId: '',
  districtId: '',
  login_disabled: false,
};

const ALL_ROLES = [
  'main_admin', 'center_body_secretary_general', 'center_body_joint_secretary', 
  'center_body_treasurer', 'center_body_auditor', 'center_body_woman_joint_secretary', 
  'district_coordinator', 'branch_secretary_general', 'branch_joint_secretary', 
  'branch_treasurer', 'branch_auditor', 'branch_counselor', 'member'
];

export default function ManageUsers() {
  const t = useT();
  const { user: currentUser } = useAuth();
  const isRTL = t.language === 'sindhi';
  
  const isMain = currentUser?.role === 'main_admin';
  const isCenterBody = currentUser?.role?.startsWith('center_body_');
  const canAddDistCoord = isMain || isCenterBody;

  const ROLES = ALL_ROLES.filter(r => {
    if (isMain) return r === 'district_coordinator'; // Main Admin only manages Councilors here
    if (r === 'district_coordinator' && !canAddDistCoord) return false;
    // main_admin and center_body roles should only be added by main_admin
    if ((r === 'main_admin' || r.startsWith('center_body_')) && !isMain) return false;
    return true;
  });

  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [districts, setDistricts] = useState([]);
  
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_USER);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      setUsers(usersSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
      
      const branchSnap = await getDocs(collection(db, 'branches'));
      setBranches(branchSnap.docs.map(d => ({ _id: d.id, ...d.data() })));

      const distSnap = await getDocs(collection(db, 'districts'));
      setDistricts(distSnap.docs.map(d => ({ _id: d.id, ...d.data() })));

    } catch (err) { 
      console.error(err); 
      toast.error("Failed to load data.");
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { load(); }, []);

  const set = (key, value) => {
    setForm(p => ({ ...p, [key]: value }));
  };

  const openAdd = () => {
    setForm({ ...EMPTY_USER, role: isMain ? 'district_coordinator' : 'member' });
    setEditId(null);
    setModal(true);
  };

  const openEdit = user => {
    setForm({ ...EMPTY_USER, ...user, password: '' });
    setEditId(user._id);
    setModal(true);
  };

  const save = async e => {
    e.preventDefault();
    try {
      const { email, password, ...userData } = form;

      if (editId) {
        await updateDoc(doc(db, 'users', editId), userData);
        toast.success(t('userUpdated'));
      } else {
        // NOTE: This is where you would call your backend function to create 
        // the user in Firebase Authentication.
        // e.g. await yourFirebaseAdmin.auth().createUser({ email, password });
        toast.success('NOTE: Password functionality requires backend setup.');
        await addDoc(collection(db, 'users'), { ...userData, email, createdAt: new Date().toISOString() });
        toast.success(t('userCreated'));
      }
      setModal(false);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Error saving user');
    }
  };

  const deleteUser = async id => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'users', id));
      toast.success(t('deletedMsg'));
      load();
    } catch { toast.error('Error'); }
  };
  
  const toggleLogin = async (id, disabled) => {
    try {
      await updateDoc(doc(db, 'users', id), { login_disabled: disabled });
      toast.success(disabled ? 'Login disabled' : 'Login enabled');
      load();
    } catch (err) {
      toast.error('Error');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = (u.displayName || '').toLowerCase().includes(search.toLowerCase()) ||
                          (u.email || '').toLowerCase().includes(search.toLowerCase());
    if (isMain) return matchesSearch && u.role === 'district_coordinator';
    return matchesSearch;
  });

  const renderName = (item) => isRTL ? (item.nameSindhi || item.name) : item.name;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {isMain ? t('manageDistrictCouncilors') : t('manageUsers')}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {isMain ? t('councilorCount', { count: filteredUsers.length }) : t('userCount', { count: filteredUsers.length })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
           <div style={{ position: 'relative', width: 250 }}>
            <span style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }}>🔍</span>
            <input className="form-input" placeholder={t('searchPlaceholder')} style={{ width: '100%', paddingLeft: isRTL ? 16 : 44, paddingRight: isRTL ? 44 : 16, height: 48, borderRadius: 14, border: '1px solid var(--sindhi-cream)', background: 'white', fontSize: 14, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn btn-primary" style={{ height: 48, padding: '0 24px', borderRadius: 12, fontSize: 14, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={openAdd}>
            {isMain ? t('addCouncilor') : t('addUser')}
          </motion.button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      ) : (
        <Card style={{ overflow: 'hidden', border: '1px solid var(--sindhi-cream)', background: 'white' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('user')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('role')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('branch')}</th>
                   <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('district')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('status')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {filteredUsers.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((user) => {
                      const branch = branches.find(b => b._id === user.branchId);
                      const district = districts.find(d => d._id === user.districtId);
                      return <motion.tr key={user._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ borderBottom: '1px solid var(--sindhi-cream)' }}>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontSize: 16 }}>{user.displayName}</div>
                          <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)' }}>{user.email}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                           <div style={{ fontSize: 14, color: 'var(--sindhi-text)' }}>{t(user.role)}</div>
                        </td>
                         <td style={{ padding: '20px 24px' }}>
                           <div style={{ fontSize: 14, fontWeight: 600 }}>{branch ? renderName(branch) : 'N/A'}</div>
                        </td>
                         <td style={{ padding: '20px 24px' }}>
                           <div style={{ fontSize: 14, fontWeight: 600 }}>{district ? renderName(district) : 'N/A'}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                           <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', background: user.login_disabled ? '#feecef' : '#e1f9eb' , color: user.login_disabled ? '#e11d48' : '#1da352' }}>
                            {user.login_disabled ? t('disabled') : t('active')}
                          </span>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <button className="btn-action" onClick={() => openEdit(user)}>{t('edit')}</button>
                            <button className="btn-action" style={{ background: user.login_disabled ? '#1da352' : '#ef4444', color: 'white' }} onClick={() => toggleLogin(user._id, !user.login_disabled)}>
                                {user.login_disabled ? t('enable') : t('disable')}
                            </button>
                            {!isMain && <button className="btn-action" style={{ background: '#feecef', color: '#e11d48' }} onClick={() => deleteUser(user._id)}>{t('delete')}</button>}
                          </div>
                        </td>
                      </motion.tr>;
                    })
                  }
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div style={{ padding: 24, direction: 'ltr' }}>
            <Pagination page={page} totalPages={Math.ceil(filteredUsers.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </Card>
      )}

      <AnimatePresence>
        {modal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="modal-box" style={{ width: 700, maxHeight: '90vh', overflowY: 'auto', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ padding: '24px 32px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 24, fontWeight: 800 }}>{editId ? t('edit') : t('add')} {isMain ? t('districtCoordinator') : t('user')}</h2>
                <button className="btn-close" onClick={() => setModal(false)}>✕</button>
              </div>
              <form onSubmit={save} style={{ padding: 32 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div className="form-group">
                    <label className="form-label">{t('displayName')} *</label>
                    <input className="form-input" value={form.displayName} onChange={e => set('displayName', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('email')} *</label>
                    <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required />
                  </div>
                  {!editId && (
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">{t('password')} *</label>
                      <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} required />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">{t('role')} *</label>
                    <select className="form-input" value={form.role} onChange={e => set('role', e.target.value)} required>
                      {ROLES.map(role => <option key={role} value={role}>{t(role)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('branch')}</label>
                    <select className="form-input" value={form.branchId} onChange={e => set('branchId', e.target.value)}>
                      <option value="">{t('noBranch')}</option>
                      {branches.map(b => <option key={b._id} value={b._id}>{renderName(b)}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t('district')}</label>
                    <select className="form-input" value={form.districtId} onChange={e => set('districtId', e.target.value)}>
                       <option value="">{t('noDistrict')}</option>
                      {districts.map(d => <option key={d._id} value={d._id}>{renderName(d)}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 32, display: 'flex', gap: 12, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 24, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button type="submit" className="btn btn-primary" style={{ height: 50, flex: 1 }}>
                    {editId ? t('save') : t('submit')}
                  </button>
                  <button type="button" onClick={() => setModal(false)} className="btn btn-outline" style={{ height: 50 }}>
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
